import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, TextField, InputAdornment, Chip, FormControl, InputLabel, Select, MenuItem,
  Tooltip, Card, CardContent, Grid,
} from '@mui/material';
import {
  Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon,
  Clear as ClearIcon, Visibility as ViewIcon, TrendingUp, TrendingDown, AccountBalance,
} from '@mui/icons-material';
import { rapprochementBancaireAPI, RapprochementItem, RapprochementSummary, encaissementsAPI, decaissementsAPI } from '../../services/api';
import AlertDialog from '../../components/AlertDialog';
import useAlert from '../../hooks/useAlert';
import { getModuleTableTheme } from '../../theme/tableTheme';
import CustomTablePagination from '../../components/CustomTablePagination';
import { extractErrorMessage } from '../../utils/errorHandler';
import PermissionButton from '../../components/PermissionButton';
import EncaissementFormDialog from './EncaissementFormDialog';
import DecaissementFormDialog from './DecaissementFormDialog';

const RapprochementBancaire: React.FC = () => {
  const [items, setItems] = useState<RapprochementItem[]>([]);
  const [summary, setSummary] = useState<RapprochementSummary>({ total_encaissements: 0, total_decaissements: 0, solde: 0 });
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [operationTypeFilter, setOperationTypeFilter] = useState<string>('');
  const [dateDebut, setDateDebut] = useState<string>('');
  const [dateFin, setDateFin] = useState<string>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Dialog states
  const [encaissementDialogOpen, setEncaissementDialogOpen] = useState(false);
  const [decaissementDialogOpen, setDecaissementDialogOpen] = useState(false);
  const [editEncaissementId, setEditEncaissementId] = useState<number | undefined>(undefined);
  const [editDecaissementId, setEditDecaissementId] = useState<number | undefined>(undefined);

  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const skip = page * rowsPerPage;
      const response = await rapprochementBancaireAPI.getPaginated(
        skip,
        rowsPerPage,
        debouncedSearch || undefined,
        dateDebut || undefined,
        dateFin || undefined,
        operationTypeFilter || undefined
      );
      setItems(response.items);
      setTotalCount(response.total);
      setSummary(response.summary);
    } catch (err) {
      showError('Erreur lors du chargement des données');
      console.error('Error fetching rapprochement data:', err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearch, dateDebut, dateFin, operationTypeFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPage(0);
  }, [operationTypeFilter, dateDebut, dateFin]);

  const handleEdit = (item: RapprochementItem) => {
    if (item.operation_type === 'encaissement') {
      setEditEncaissementId(item.id);
      setEncaissementDialogOpen(true);
    } else {
      setEditDecaissementId(item.id);
      setDecaissementDialogOpen(true);
    }
  };

  const handleDelete = async (item: RapprochementItem) => {
    const typeLabel = item.operation_type === 'encaissement' ? "l'encaissement" : 'le décaissement';
    showConfirm(
      `Êtes-vous sûr de vouloir supprimer ${typeLabel} "${item.reference}" ?`,
      async () => {
        try {
          if (item.operation_type === 'encaissement') {
            await encaissementsAPI.delete(item.id);
          } else {
            await decaissementsAPI.delete(item.id);
          }
          showSuccess(`${item.operation_type === 'encaissement' ? 'Encaissement' : 'Décaissement'} supprimé avec succès`);
          fetchData();
        } catch (err: any) {
          showError(extractErrorMessage(err));
        }
      },
      {
        title: 'Confirmer la suppression',
        confirmText: 'Supprimer',
        cancelText: 'Annuler',
      }
    );
  };

  const handleFormSuccess = () => {
    setEncaissementDialogOpen(false);
    setDecaissementDialogOpen(false);
    setEditEncaissementId(undefined);
    setEditDecaissementId(undefined);
    fetchData();
  };

  const formatAmount = (amount: number) =>
    new Intl.NumberFormat('fr-MA', { style: 'currency', currency: 'MAD' }).format(amount);

  const getSigneChip = (signe: string) => {
    return signe === '+' ? (
      <Chip label="+" color="success" size="small" sx={{ fontWeight: 'bold', minWidth: 30 }} />
    ) : (
      <Chip label="-" color="error" size="small" sx={{ fontWeight: 'bold', minWidth: 30 }} />
    );
  };

  const getOperationTypeChip = (type: string) => {
    return type === 'encaissement' ? (
      <Chip label="Encaissement" color="success" size="small" variant="outlined" />
    ) : (
      <Chip label="Décaissement" color="error" size="small" variant="outlined" />
    );
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'en_cours':
        return <Chip label="En Cours" color="warning" size="small" />;
      case 'encaisse':
        return <Chip label="Encaissé" color="success" size="small" />;
      case 'impaye':
        return <Chip label="Impayé" color="error" size="small" />;
      case 'payé':
      case 'paye':
        return <Chip label="Payé" color="success" size="small" />;
      default:
        return <Chip label={status || '-'} size="small" />;
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" color="primary">
          Rapprochement Bancaire
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: '#e8f5e9' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TrendingUp sx={{ fontSize: 40, color: '#4caf50' }} />
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Total Encaissements</Typography>
                <Typography variant="h5" fontWeight="bold" color="success.main">
                  {formatAmount(summary.total_encaissements)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: '#ffebee' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TrendingDown sx={{ fontSize: 40, color: '#f44336' }} />
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Total Décaissements</Typography>
                <Typography variant="h5" fontWeight="bold" color="error.main">
                  {formatAmount(summary.total_decaissements)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: summary.solde >= 0 ? '#e3f2fd' : '#fff3e0' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <AccountBalance sx={{ fontSize: 40, color: summary.solde >= 0 ? '#2196f3' : '#ff9800' }} />
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Solde</Typography>
                <Typography variant="h5" fontWeight="bold" color={summary.solde >= 0 ? 'info.main' : 'warning.main'}>
                  {formatAmount(summary.solde)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Rechercher..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}><ClearIcon /></IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ width: 250 }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Type Opération</InputLabel>
          <Select
            value={operationTypeFilter}
            label="Type Opération"
            onChange={(e) => setOperationTypeFilter(e.target.value)}
          >
            <MenuItem value="">Tous</MenuItem>
            <MenuItem value="encaissement">Encaissements</MenuItem>
            <MenuItem value="decaissement">Décaissements</MenuItem>
          </Select>
        </FormControl>
        <TextField
          size="small"
          type="date"
          label="Date début"
          value={dateDebut}
          onChange={(e) => setDateDebut(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 160 }}
        />
        <TextField
          size="small"
          type="date"
          label="Date fin"
          value={dateFin}
          onChange={(e) => setDateFin(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 160 }}
        />
        {(dateDebut || dateFin || operationTypeFilter || searchTerm) && (
          <IconButton
            size="small"
            onClick={() => {
              setDateDebut('');
              setDateFin('');
              setOperationTypeFilter('');
              setSearchTerm('');
            }}
            sx={{ alignSelf: 'center' }}
          >
            <Tooltip title="Effacer les filtres">
              <ClearIcon />
            </Tooltip>
          </IconButton>
        )}
      </Box>

      {/* Table */}
      <TableContainer component={Paper} sx={getModuleTableTheme('financier')}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 90 }}>Date</TableCell>
              <TableCell sx={{ width: 90 }}>Échéance</TableCell>
              <TableCell sx={{ width: 180 }}>Libellé</TableCell>
              <TableCell sx={{ width: 160 }}>Source</TableCell>
              <TableCell sx={{ width: 110 }}>Type Op.</TableCell>
              <TableCell sx={{ width: 110 }}>Mode Paiement</TableCell>
              <TableCell sx={{ width: 110 }}>Réf. Paiement</TableCell>
              <TableCell align="right" sx={{ width: 120 }}>Montant</TableCell>
              <TableCell align="center" sx={{ width: 50 }}>+/-</TableCell>
              <TableCell sx={{ width: 80 }}>État</TableCell>
              <TableCell align="center" sx={{ width: 100 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={11} align="center">Chargement...</TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center">Aucune opération bancaire trouvée</TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <TableRow key={`${item.operation_type}-${item.id}`} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {new Date(item.date).toLocaleDateString('fr-FR')}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {item.date_echeance ? new Date(item.date_echeance).toLocaleDateString('fr-FR') : '-'}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <Tooltip title={item.libelle || ''} arrow placement="top">
                      <span>{item.libelle || '-'}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <Tooltip title={item.source || ''} arrow placement="top">
                      <span>{item.source || '-'}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {getOperationTypeChip(item.operation_type)}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <Tooltip title={item.mode_paiement_nom || ''} arrow placement="top">
                      <span>{item.mode_paiement_nom || '-'}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <Tooltip title={item.reference_paiement || ''} arrow placement="top">
                      <span>{item.reference_paiement || '-'}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 500 }}>
                    {formatAmount(item.montant)}
                  </TableCell>
                  <TableCell align="center">
                    {getSigneChip(item.signe)}
                  </TableCell>
                  <TableCell>
                    {getStatusChip(item.status)}
                  </TableCell>
                  <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                      <PermissionButton
                        moduleName="Financier"
                        operation="edit"
                        miniModuleName={item.operation_type === 'encaissement' ? 'Encaissements' : 'Decaissements'}
                        component="IconButton"
                        onClick={() => handleEdit(item)}
                        title="Modifier"
                      >
                        <EditIcon fontSize="small" />
                      </PermissionButton>
                      <PermissionButton
                        moduleName="Financier"
                        operation="delete"
                        miniModuleName={item.operation_type === 'encaissement' ? 'Encaissements' : 'Decaissements'}
                        component="IconButton"
                        onClick={() => handleDelete(item)}
                        color="error"
                        title="Supprimer"
                      >
                        <DeleteIcon fontSize="small" />
                      </PermissionButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <CustomTablePagination
        count={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_, p) => setPage(p)}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
      />

      {/* Encaissement Edit Dialog */}
      <EncaissementFormDialog
        open={encaissementDialogOpen}
        onClose={() => { setEncaissementDialogOpen(false); setEditEncaissementId(undefined); }}
        onSuccess={handleFormSuccess}
        encaissementId={editEncaissementId}
        showStatus
      />

      {/* Decaissement Edit Dialog */}
      <DecaissementFormDialog
        open={decaissementDialogOpen}
        onClose={() => { setDecaissementDialogOpen(false); setEditDecaissementId(undefined); }}
        onSuccess={handleFormSuccess}
        editId={editDecaissementId}
      />

      <AlertDialog {...alertState} onClose={hideAlert} onConfirm={alertState.onConfirm || hideAlert} />
    </Box>
  );
};

export default RapprochementBancaire;
